import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  NotificationsActive as NotificationsIcon,
  School as SchoolIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    let base = process.env.REACT_APP_API_URL;
    base = base.endsWith('/') ? base.slice(0, -1) : base;
    if (base.endsWith('/api')) base = base.slice(0, -4);
    return base;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '';
  }
  return 'http://localhost:3000';
};

const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
  if (typeof window !== 'undefined') {
    if (window.location.hostname.includes('services.knls.in')) {
      return 'https://locsy-lq54y.ondigitalocean.app';
    }
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const ICON_MAP = {
  school: SchoolIcon,
  store: StoreIcon,
  people: PeopleIcon,
};

const PRIORITY_COLORS = {
  low: 'default',
  normal: 'primary',
  high: 'warning',
  urgent: 'error',
};

const AppNotifications = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({});
  const [history, setHistory] = useState([]);
  const [selectedApp, setSelectedApp] = useState('knls_students');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [liveToast, setLiveToast] = useState(null);

  const [form, setForm] = useState({
    title: '',
    message: '',
    notification_type: 'general',
    priority: 'normal',
    action_url: '',
    action_label: '',
    audience_type: 'all',
    course_code: '',
    phones: '',
  });

  const apiBase = getApiBaseUrl();
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    [],
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [appsRes, statsRes, historyRes] = await Promise.all([
        axios.get(`${apiBase}/api/app-notifications/apps`, { headers: authHeaders }),
        axios.get(`${apiBase}/api/app-notifications/stats`, { headers: authHeaders }),
        axios.get(`${apiBase}/api/app-notifications`, {
          headers: authHeaders,
          params: { target_app: selectedApp, limit: 30 },
        }),
      ]);
      setApps(appsRes.data?.data || []);
      setStats(statsRes.data?.data || {});
      setHistory(historyRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load app notifications');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders, selectedApp]);

  useEffect(() => {
    document.title = 'App Notifications — LOCSY Admin';
    return () => {
      document.title = 'LOCSY Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = io(getSocketUrl(), {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token },
    });

    socket.on('connect', () => {
      socket.emit('join-app-notifications', selectedApp);
    });

    socket.on('app-notification', (payload) => {
      if (payload?.target_app === selectedApp) {
        setLiveToast(`Live: "${payload.title}" delivered`);
        fetchAll();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [selectedApp, fetchAll]);

  const handleSend = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      setError('Title and message are required');
      return;
    }

    setSending(true);
    setError(null);
    try {
      const audience_filter = {};
      if (form.audience_type === 'course' && form.course_code.trim()) {
        audience_filter.course_code = form.course_code.trim();
      }
      if (form.audience_type === 'phones' && form.phones.trim()) {
        audience_filter.phones = form.phones
          .split(/[\n,;]+/)
          .map((p) => p.trim())
          .filter(Boolean);
      }

      const res = await axios.post(
        `${apiBase}/api/app-notifications`,
        {
          title: form.title.trim(),
          message: form.message.trim(),
          target_app: selectedApp,
          notification_type: form.notification_type,
          priority: form.priority,
          action_url: form.action_url.trim() || undefined,
          action_label: form.action_label.trim() || undefined,
          audience_type: form.audience_type,
          audience_filter,
          send_now: true,
        },
        { headers: authHeaders },
      );

      setSuccess(
        `Sent to ${res.data?.data?.sent_count ?? 0} user(s) on ${
          apps.find((a) => a.key === selectedApp)?.title || selectedApp
        }`,
      );
      setForm((prev) => ({ ...prev, title: '', message: '', phones: '' }));
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const selectedAppMeta = apps.find((a) => a.key === selectedApp);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <NotificationsIcon color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h4" fontWeight={800}>
            App Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send real-time alerts to KNLS Students (LMS), Mitra, and Citizen apps
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {apps.map((app) => {
          const Icon = ICON_MAP[app.icon] || NotificationsIcon;
          const active = selectedApp === app.key;
          const appStats = stats[app.key] || {};
          return (
            <Grid item xs={12} md={4} key={app.key}>
              <Card
                sx={{
                  border: active ? `2px solid ${app.color}` : '1px solid',
                  borderColor: active ? app.color : 'divider',
                  boxShadow: active ? 4 : 1,
                }}
              >
                <CardActionArea onClick={() => setSelectedApp(app.key)}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          bgcolor: `${app.color}18`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon sx={{ color: app.color }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {app.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {appStats.total_sent || 0} campaigns · {appStats.total_deliveries || 0}{' '}
                          delivered
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {app.description}
                    </Typography>
                    {active && (
                      <Chip label="Selected" size="small" color="primary" sx={{ mt: 1.5 }} />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Compose — {selectedAppMeta?.title || selectedApp}
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                fullWidth
                multiline
                minRows={3}
                required
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={form.notification_type}
                    onChange={(e) => setForm({ ...form, notification_type: e.target.value })}
                  >
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="announcement">Announcement</MenuItem>
                    <MenuItem value="alert">Alert</MenuItem>
                    <MenuItem value="reminder">Reminder</MenuItem>
                    <MenuItem value="update">Update</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <FormControl fullWidth>
                <InputLabel>Audience</InputLabel>
                <Select
                  label="Audience"
                  value={form.audience_type}
                  onChange={(e) => setForm({ ...form, audience_type: e.target.value })}
                >
                  <MenuItem value="all">All users on this app</MenuItem>
                  {selectedApp === 'knls_students' && (
                    <MenuItem value="course">Students in a course</MenuItem>
                  )}
                  <MenuItem value="phones">Specific phone numbers</MenuItem>
                </Select>
              </FormControl>
              {form.audience_type === 'course' && (
                <TextField
                  label="Course code"
                  value={form.course_code}
                  onChange={(e) => setForm({ ...form, course_code: e.target.value })}
                  placeholder="e.g. DCA, PGDCA"
                  fullWidth
                />
              )}
              {form.audience_type === 'phones' && (
                <TextField
                  label="Phone numbers"
                  value={form.phones}
                  onChange={(e) => setForm({ ...form, phones: e.target.value })}
                  placeholder="Comma or newline separated"
                  fullWidth
                  multiline
                  minRows={2}
                />
              )}
              <TextField
                label="Action URL (optional)"
                value={form.action_url}
                onChange={(e) => setForm({ ...form, action_url: e.target.value })}
                placeholder="/courses or https://..."
                fullWidth
              />
              <TextField
                label="Action button label"
                value={form.action_label}
                onChange={(e) => setForm({ ...form, action_label: e.target.value })}
                placeholder="Open"
                fullWidth
              />
              <Button
                variant="contained"
                size="large"
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? 'Sending…' : 'Send notification'}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Recent sends
              </Typography>
              <Button startIcon={<RefreshIcon />} onClick={fetchAll} disabled={loading}>
                Refresh
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell align="right">Sent</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No notifications sent yet for this app
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {row.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {row.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={row.notification_type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.priority}
                              size="small"
                              color={PRIORITY_COLORS[row.priority] || 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">{row.sent_count}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {row.sent_at
                                ? new Date(row.sent_at).toLocaleString()
                                : new Date(row.created_at).toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={5000}
        onClose={() => setSuccess(null)}
        message={success}
      />
      <Snackbar
        open={Boolean(liveToast)}
        autoHideDuration={4000}
        onClose={() => setLiveToast(null)}
        message={liveToast}
      />
    </Container>
  );
};

export default AppNotifications;
